import React from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { Check, X } from 'lucide-react';
import { useNavigate } from 'react-router';
import { useTheme } from '../../contexts/ThemeContext';
import { useBilling } from '../../contexts/BillingContext';
import { Z } from '../../constants/zIndex';
import Button from '../ui/Button';

const BillingPaymentSuccessOverlay = () => {
  const { preference } = useTheme();
  const { paymentSuccessStatus, clearPaymentSuccess } = useBilling();
  const navigate = useNavigate();

  const isLight = preference === 'light';

  if (!paymentSuccessStatus) {
    return null;
  }

  const surfaceBg = isLight ? '#ffffff' : '#1a2335';
  const textPrimary = isLight ? '#0f172a' : '#ffffff';
  const textMuted = isLight ? 'rgba(15,23,42,0.55)' : 'rgba(255,255,255,0.55)';
  const borderColor = isLight ? 'rgba(15,23,42,0.08)' : 'rgba(255,255,255,0.08)';
  const closeHover = isLight ? 'hover:bg-black/5' : 'hover:bg-white/8';

  const handleGoToDashboard = () => {
    clearPaymentSuccess();
    navigate('/dashboard');
  };

  const handleManageBilling = () => {
    clearPaymentSuccess();
    navigate('/settings?section=billing');
  };

  return createPortal(
    <AnimatePresence>
      <motion.div
        key="billing-success-backdrop"
        className="fixed inset-0 backdrop-blur-sm"
        style={{ zIndex: Z.BILLING_PAYMENT_MODAL - 1, backgroundColor: 'rgba(0,0,0,0.55)' }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.18 }}
        onClick={clearPaymentSuccess}
      />

      <motion.div
        key="billing-success-modal"
        className="fixed inset-0 flex items-center justify-center px-4"
        style={{ zIndex: Z.BILLING_PAYMENT_MODAL }}
        initial={{ opacity: 0, scale: 0.97, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.97, y: 8 }}
        transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
      >
        <div
          className="relative w-full max-w-md rounded-2xl shadow-2xl"
          style={{ backgroundColor: surfaceBg, border: `1px solid ${borderColor}` }}
          onClick={(e) => e.stopPropagation()}
        >
          <Button
            type="button"
            variant="ghost"
            size="iconSm"
            onClick={clearPaymentSuccess}
            className={`absolute right-4 top-4 flex h-9 w-9 shrink-0 items-center justify-center rounded-full transition-colors ${closeHover}`}
            style={{ color: textMuted }}
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </Button>

          <div className="space-y-8 px-6 pb-6 pt-12">
            <div className="flex flex-col items-center gap-4 text-center">
              <div className={`flex h-14 w-14 items-center justify-center rounded-full ${
                isLight ? 'bg-emerald-500/10 text-emerald-600' : 'bg-emerald-500/15 text-emerald-400'
              }`}>
                <Check className="h-7 w-7" strokeWidth={2.4} />
              </div>
              <h2 className="text-[2rem] font-semibold leading-tight tracking-normal" style={{ color: textPrimary }}>
                You're all set
              </h2>
            </div>

            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <Button type="button" fullWidth onClick={handleGoToDashboard}>
                Go to Dashboard
              </Button>
              <Button type="button" variant="secondary" fullWidth onClick={handleManageBilling}>
                Manage Billing
              </Button>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>,
    document.body
  );
};

export default BillingPaymentSuccessOverlay;
